import React from "react";
import { useHistory } from "react-router-dom";

import {
	faAngleDown,
	faAngleUp,
	faCalendar,
	faCalendarMinus,
	faCalendarPlus,
	faCheckSquare,
	faMapMarker,
	faPhone,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV, faPen } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCheck, faQuestion, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact } from "lodash";
import { commitMutation, fetchQuery, graphql } from "relay-runtime";

import formatEventDescription from "~/calendar/utils/formatEventDescription";
import type { AttendeeAttrs } from "~/common/Attendees";
import type { MeetingEvent } from "~/common/MeetingSelect";
import { MeetingInvite } from "~/invite";
import { confirm } from "~/lib/prompts";
import { isDesktop, meetingDateFormat } from "~/lib/utils";
import { RecordIcon, shouldShowRecordingIcon } from "~/meetingRecorder/MeetingRecordingBanner";
import { environment } from "~/models";
import { AgendaContext } from "~/nav/panels/Meetings";
import { invalidateHiddenEvent } from "~/nav/panels/Meetings/Events";
import { getActiveCalendar } from "~/nav/panels/Meetings/utils";
import NoteAndMeetingLinkerModal from "~/stream/NoteAndMeetingLinkerModal";
import StreamSettingsModal from "~/stream/StreamSettings";
import { recurrenceRuleToString } from "~/stream/utils";
import { CallIcon, RecordingsIcon } from "~/stream/VideoCallButton";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { ButtonLink, IconButton, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { __globalModalContext } from "~/ui/modals";

import type { MeetingInfoModal_HideEventMutation as HideEventMutationQuery } from "~/__generated__/MeetingInfoModal_HideEventMutation.graphql";
import type { MeetingInfoModalQuery } from "~/__generated__/MeetingInfoModalQuery.graphql";
import type { ResponseStatusChoices } from "~/__generated__/MeetingsPanelQuery.graphql";
import type { NoteStreamType } from "~/__generated__/NoteCard_note.graphql";
import type { NoteTimeSelectorModal_note$key } from "~/__generated__/NoteTimeSelectorModal_note.graphql";

import styles from "~/nav/panels/Meetings/MeetingInfoModal.module.css";

export function urlToAnchorTag(text: string, newWindow = false) {
	try {
		new URL(text);
	} catch (e) {
		return text;
	}

	const onClick = (e: React.MouseEvent) => {
		const isModifierActive = ["Alt", "AltGraph", "Control", "Hyper", "Meta", "OS", "Shift", "Super"].some(key =>
			e.getModifierState(key),
		);

		if (!newWindow || isModifierActive) return;

		e.stopPropagation();

		const w = window.screen.availWidth / 2;
		const h = window.screen.availHeight / 2;

		const windowFeatures = `width=${w},height=${h}`;

		window.open(text, "_blank", windowFeatures);
	};

	return (
		<a target="_blank" rel="noopener noreferrer" href={text} onClick={onClick}>
			{text}
		</a>
	);
}

export function MeetingInfoModalAttendeesList({ eventId, attendees }: { eventId: string; attendees: AttendeeAttrs[] }) {
	const [showMoreSelected, setShowMoreSelected] = React.useState(false);
	const [inviteUser, setInviteUser] = React.useState<AttendeeAttrs | null>(null);

	const onShowMoreClick = React.useCallback(() => {
		setShowMoreSelected(true);
	}, []);

	const onShowLessClick = React.useCallback(() => {
		setShowMoreSelected(false);
	}, []);

	const clearInviteUser = React.useCallback(() => {
		setInviteUser(null);
	}, []);

	const onAttendeeInviteClick = React.useCallback(
		(attendee: AttendeeAttrs) => () => {
			if (attendee?.invitable) setInviteUser(attendee);
		},
		[],
	);

	const attendeesTotal = attendees.length;
	const attendeesAccepted = attendees.filter(
		a =>
			a?.responseStatus === "accepted" ||
			(a?.isOrganizer && ["needsAction", "organizer"].includes(a.responseStatus || "")),
	).length;
	const attendeesDeclined = attendees.filter(a => a.responseStatus === "declined").length;
	const attendeesMaybe = attendees.filter(
		a => a.responseStatus === "tentative" || a.responseStatus === "tentativelyAccepted",
	).length;
	const attendeesAwaiting = attendeesTotal - attendeesAccepted - attendeesDeclined - attendeesMaybe;

	const texts = [
		attendeesAccepted > 0 ? `${attendeesAccepted} yes` : "",
		attendeesDeclined > 0 ? `${attendeesDeclined} no` : "",
		attendeesMaybe > 0 ? `${attendeesMaybe} maybe` : "",
		attendeesAwaiting > 0 ? `${attendeesAwaiting} awaiting` : "",
	].filter(Boolean);

	const sortedAttendees = attendees.sort((a, b) => {
		if (a.isOrganizer && !b.isOrganizer) return -1;
		if (!a.isOrganizer && b.isOrganizer) return 1;

		if (a.responseStatus === "accepted" && b.responseStatus !== "accepted") return -1;
		if (a.responseStatus !== "accepted" && b.responseStatus === "accepted") return 1;

		if (a.responseStatus === "declined" && b.responseStatus !== "declined") return -1;
		if (a.responseStatus !== "declined" && b.responseStatus === "declined") return 1;

		if (a.responseStatus === "tentative" && b.responseStatus !== "tentative") return -1;
		if (a.responseStatus !== "tentative" && b.responseStatus === "tentative") return 1;

		if (a.responseStatus === "tentativelyAccepted" && b.responseStatus !== "tentativelyAccepted") return -1;
		if (a.responseStatus !== "tentativelyAccepted" && b.responseStatus === "tentativelyAccepted") return 1;

		return a.fullName && b.fullName ? a.fullName.localeCompare(b.fullName) : a.email.localeCompare(b.email);
	});

	return (
		<>
			<div className={styles.attendeeCount} data-test-id="meeting-info-modal-attendeecount">
				{attendeesTotal} Invited · {texts.join(", ")}
			</div>

			<div className={cx({ [styles.attendeesBigPlaceholder]: attendeesAwaiting < attendeesTotal })}>
				{sortedAttendees.slice(0, showMoreSelected ? 20 : 10).map(attendee => {
					if (!attendee) return null;
					return (
						<MeetingInfoModalAttendee
							key={attendee.user?.id || attendee.email || ""}
							attendee={attendee}
							onInviteClick={onAttendeeInviteClick(attendee as AttendeeAttrs)}
						/>
					);
				})}

				{sortedAttendees.length > 10 &&
					(!showMoreSelected ? (
						<span className={styles.showMoreLessButton} onClick={onShowMoreClick}>
							show more {<FontAwesomeIcon icon={faAngleDown} />}
						</span>
					) : (
						<>
							{sortedAttendees.length > 20 && (
								<div className={styles.attendee} data-test-id="meeting-info-modal-attendee">
									<div className={styles.attendeeStatus}>
										<Avatar
											initials={`+${sortedAttendees.length - 20}`}
											width="1.5em"
											uiAvatarOptions={{
												background: "F5F6F9",
												color: "6F787D",
											}}
										/>
										<div className={cx(styles.attendeeResponse, styles.placeholder)} />
									</div>
									<div className={styles.attendeeInfo}>
										<p className={styles.attendeeName}>+{sortedAttendees.length - 20} others</p>
									</div>
								</div>
							)}
							<span className={styles.showMoreLessButton} onClick={onShowLessClick}>
								show less {<FontAwesomeIcon icon={faAngleUp} />}
							</span>
						</>
					))}

				{sortedAttendees.some(attendee => attendee?.invitable) && Boolean(inviteUser) && (
					<MeetingInvite
						user={inviteUser}
						// eslint-disable-next-line
						onClose={clearInviteUser}
						eventId={eventId}
						attendees={sortedAttendees.filter(item => item?.email)}
						// eslint-disable-next-line
						analytics={{ ux_location: "meetings__event_info_modal" }}
					/>
				)}
			</div>
		</>
	);
}

const MeetingInfoModalAttendee = ({
	attendee,
	onInviteClick,
}: {
	attendee: AttendeeAttrs;
	onInviteClick: () => void;
}) => {
	if (!attendee) return null;
	let status: React.ReactNode = <div className={cx(styles.attendeeResponse, styles.placeholder)} />;
	if (attendee.responseStatus === "accepted") {
		status = (
			<div className={cx(styles.attendeeResponse, styles.attending)}>
				<FontAwesomeIcon fixedWidth icon={faCheck} />
			</div>
		);
	} else if (attendee.responseStatus === "declined") {
		status = (
			<div className={cx(styles.attendeeResponse, styles.declined)}>
				<FontAwesomeIcon fixedWidth icon={faTimes} />
			</div>
		);
	} else if (attendee.responseStatus === "tentative" || attendee.responseStatus === "tentativelyAccepted") {
		status = (
			<div className={cx(styles.attendeeResponse, styles.tentative)}>
				<FontAwesomeIcon fixedWidth icon={faQuestion} />
			</div>
		);
	}

	return (
		<div className={styles.attendee} data-test-id="meeting-info-modal-attendee">
			<div className={styles.attendeeStatus}>
				<Avatar
					className={cx({ [styles.attendeeAvatarInvitable]: attendee.invitable })}
					user={{
						...attendee,
						id: attendee.user?.id || "",
						fullName: attendee.fullName || attendee.email || "",
						isTopUser: attendee.user?.isTopUser || false,
					}}
					link={false}
					width="1rem"
					hideDiamond
				/>
				{status}
			</div>

			<div className={styles.attendeeInfo}>
				<p className={styles.attendeeName}>{attendee.fullName}</p>
				{attendee.isOrganizer ? (
					<p className={styles.attendeeJob}>Organizer</p>
				) : attendee.isOptional ? (
					<p className={styles.attendeeJob}>Optional</p>
				) : null}
			</div>

			{attendee.invitable && (
				<Button size="slim" variant="secondary" className={styles.attendeeInviteButton} onClick={onInviteClick}>
					Invite
				</Button>
			)}
		</div>
	);
};

const HideEventMutation = graphql`
	mutation MeetingInfoModal_HideEventMutation($guid: String!) {
		hideEvent(guid: $guid) {
			ok
		}
	}
`;

export function MeetingInfoModalWrapper({
	eventGuid,
	onClose,
	note,
	noteType,
	hideEventPressed,
	isExternalUser,
	onChangeLinkedMeeting,
}: {
	eventGuid: string;
	onClose: () => void;
	note?: NoteTimeSelectorModal_note$key;
	noteType?: NoteStreamType;
	hideEventPressed?: () => void;
	isExternalUser?: boolean;
	onChangeLinkedMeeting?: (event: MeetingEvent) => void;
}) {
	return (
		<AgendaContext.Consumer>
			{contextProps => {
				const editClick = () => {
					contextProps.openEditModal(eventGuid);
					onClose();
				};
				const isFromMeetingsPanel = contextProps.calendars.length > 0;
				const calendars = contextProps.calendars;
				const activeCalendar = getActiveCalendar(calendars);
				const handleUserStatusResponse = (userResponseStatus: ResponseStatusChoices) => {
					activeCalendar
						? contextProps.answerEventAttendance(activeCalendar, eventGuid, userResponseStatus, () => onClose())
						: null;
				};
				return (
					<MeetingInfoModal
						onClose={onClose}
						eventGuid={eventGuid}
						noteKey={note}
						isFromMeetingsPanel={isFromMeetingsPanel}
						// eslint-disable-next-line react/jsx-no-bind
						onEditClick={editClick}
						isExternalUser={Boolean(isExternalUser)}
						noteType={noteType}
						onChangeLinkedMeeting={onChangeLinkedMeeting}
						hideEventPressed={hideEventPressed}
						// eslint-disable-next-line react/jsx-no-bind
						handleUserStatusResponse={handleUserStatusResponse}
					/>
				);
			}}
		</AgendaContext.Consumer>
	);
}

export function MeetingInfoModal({
	onClose,
	eventGuid,
	noteKey,
	isFromMeetingsPanel,
	onEditClick,
	isExternalUser,
	noteType,
	onChangeLinkedMeeting,
	hideEventPressed,
	handleUserStatusResponse,
}: {
	onClose: () => void;
	eventGuid: string;
	noteKey?: NoteTimeSelectorModal_note$key;
	isFromMeetingsPanel: boolean;
	onEditClick: () => void;
	isExternalUser: boolean;
	noteType?: NoteStreamType;
	onChangeLinkedMeeting: ((event: MeetingEvent) => void) | undefined;
	handleUserStatusResponse: (userResponseStatus: ResponseStatusChoices) => void;
	hideEventPressed?: () => void;
}) {
	const [event, setEvent] = React.useState<MeetingInfoModalQuery["response"]["event"] | null>(null);
	const [loading, setLoading] = React.useState(true);

	React.useEffect(() => {
		setLoading(true);
		fetchQuery<MeetingInfoModalQuery>(
			environment,
			graphql`
				query MeetingInfoModalQuery($guid: String) {
					event: eventByGuid(guid: $guid) {
						... on Event {
							id
							guid
							title
							description
							start
							end
							isAllDay
							recurrenceRule
							isOrganizer
							status
							userResponseStatus
							calendar {
								sourcePrimary
								color
								name
							}
							location
							conferenceCalls {
								provider
								url
								phoneNumber
								pin
							}
							isHidden
							userTodos {
								done
							}
							stream {
								id
								schedulingMeetingAssistantAutomationIsActive
							}
							attachments {
								provider
								fileId
								url
								fileType
								title
							}
							attendees {
								__typename
								email
								fullName
								image
								invitable
								title
								responseStatus
								isOptional
								isOrganizer
								user {
									id
									isTopUser
								}
							}
						}
					}
				}
			`,
			{ guid: eventGuid },
		)
			.toPromise()
			.then((response: MeetingInfoModalQuery["response"] | undefined) => {
				setLoading(false);
				setEvent(response?.event ?? null);
			});
	}, [eventGuid]);

	const meetingDescriptionRef = React.useRef<HTMLParagraphElement>(null);

	const [hideMutationLoading, setHideMutationLoading] = React.useState(false);
	const [showMoreDescription, setShowMoreDescription] = React.useState(false);

	const descriptionExpandable = React.useMemo(() => (meetingDescriptionRef.current?.scrollHeight ?? 0) > 68, []);

	const onDescriptionMoreClick = React.useCallback(() => {
		setShowMoreDescription(true);
	}, []);

	const onDescriptionLessClick = React.useCallback(() => {
		setShowMoreDescription(false);
	}, []);

	const hasEdit = isFromMeetingsPanel && event?.isOrganizer && event?.status !== "cancelled";

	const onConfirmChange = React.useCallback(
		(event: MeetingEvent) => {
			onChangeLinkedMeeting?.(event);
			onClose();
		},
		[onChangeLinkedMeeting, onClose],
	);

	const showRecordIcon = React.useMemo(
		() =>
			event &&
			shouldShowRecordingIcon(event.stream?.schedulingMeetingAssistantAutomationIsActive ?? false, {
				...event,
				status: event.status,
			}),
		[event],
	);

	const history = useHistory();
	const onClickOpenNotes = React.useCallback(() => {
		onClose();
		if (event) {
			history.push(`/meetings/${event?.guid}/`);
		}
	}, [event, history, onClose]);

	const openStreamSettingsModal = React.useCallback(() => {
		const streamId = event?.stream?.id;
		if (streamId) {
			__globalModalContext?.open(({ close }) => (
				<StreamSettingsModal close={close} streamId={streamId} defaultSection="automations" />
			));
		}
	}, [event]);

	const openNoteLinkingModal = React.useCallback(() => {
		if (noteKey && onChangeLinkedMeeting) {
			__globalModalContext?.open(({ close }) => (
				<NoteAndMeetingLinkerModal
					closeModal={close}
					currentEvent={
						event
							? {
									guid: event?.guid,
									title: event?.title || "",
									start: event?.start,
									end: event?.end,
								}
							: undefined
					}
					note={noteKey}
					onChangeLinkedMeeting={onConfirmChange}
				/>
			));
		}
	}, [event, noteKey, onChangeLinkedMeeting, onConfirmChange]);

	const remainingActionItems = React.useMemo(() => {
		const userTodos = event?.userTodos;
		if (userTodos?.length === 0) {
			return "No action items have been assigned to you";
		}
		const userOutstandingTodos = userTodos?.filter(todo => todo && !todo.done);
		if (userOutstandingTodos?.length === 0) {
			return "All of your action items from this meeting are complete";
		}
		return `You have ${userOutstandingTodos?.length ?? 0} incomplete action items`;
	}, [event?.userTodos]);

	const hideEvent = React.useCallback(() => {
		if (!event) {
			return;
		}

		const message = event.isHidden ? (
			"Showing a recurring event will also show all events in the series."
		) : (
			<>
				<span>
					You can reveal this event by selecting <i>Show hidden meetings</i> in the filters menu&ensp;
					<FontAwesomeIcon icon={faEllipsisV} size="1x" />
					&ensp;at the top of the meetings panel.
				</span>
				<br />
				<br />
				<span>Hiding a recurring event will also hide all events in the series.</span>
			</>
		);

		confirm(message, {
			yesText: event.isHidden ? "Show" : "Hide",
			title: event.isHidden ? "Show selected event again?" : "Hide selected event?",
		}).then(result => {
			if (!result) {
				return;
			}

			setHideMutationLoading(true);

			commitMutation<HideEventMutationQuery>(environment, {
				mutation: HideEventMutation,
				variables: { guid: event.guid },
				onCompleted: ({ hideEvent }) => {
					setHideMutationLoading(false);
					if (hideEvent?.ok && hideEventPressed) {
						hideEventPressed();
						// Assuming invalidateHiddenEvent is a function defined elsewhere
						invalidateHiddenEvent(event.id);
					}
				},
			});
		});
	}, [event, hideEventPressed]);

	const handleAcceptedResponse = React.useCallback(() => {
		handleUserStatusResponse("accepted");
	}, [handleUserStatusResponse]);

	const handleDeclinedResponse = React.useCallback(() => {
		handleUserStatusResponse("declined");
	}, [handleUserStatusResponse]);

	const handleTenativeResponse = React.useCallback(() => {
		handleUserStatusResponse("tentative");
	}, [handleUserStatusResponse]);

	const callContent = (
		<>
			{event &&
				event.conferenceCalls.length > 0 &&
				event?.conferenceCalls.map(call => (
					<React.Fragment key={call.url}>
						{call.url && (
							<div className={styles.row} data-test-id="meeting-info-modal-video">
								<div className={styles.rowIconSmall}>
									<CallIcon provider={call.provider} />
								</div>
								<div className={styles.rowInfo}>
									<p>Join Video Call</p>
									{urlToAnchorTag(call.url, isDesktop)}
								</div>
							</div>
						)}

						{call.phoneNumber && (
							<div className={styles.row} data-test-id="meeting-info-modal-phone">
								<div className={styles.rowIcon}>
									<FontAwesomeIcon fixedWidth icon={faPhone} />
								</div>
								<div className={styles.rowInfo}>
									<p>Join by Phone</p>
									<p>{call.phoneNumber}</p>
									{call.pin && <p>PIN {call.pin}#</p>}
								</div>
							</div>
						)}
					</React.Fragment>
				))}

			{isFromMeetingsPanel &&
				(hideMutationLoading ? (
					<div className={styles.loader}>
						<LoadingSpinner />
					</div>
				) : (
					<div className={styles.row}>
						<div className={styles.rowIcon}>
							<FontAwesomeIcon fixedWidth icon={event?.isHidden ? faCalendarPlus : faCalendarMinus} />
						</div>
						<div className={styles.rowInfo}>
							<ButtonLink onClick={hideEvent}>
								{event?.isHidden ? "Display event again" : "Don’t show this event in Fellow"}
							</ButtonLink>
						</div>
					</div>
				))}
		</>
	);

	const newDescription = React.useMemo(() => formatEventDescription(event?.description || ""), [event?.description]);

	const description =
		newDescription && /\S/.test(newDescription) ? (
			<div data-test-id="meeting-info-modal-description">
				<h6 className={styles.meetingHeader}>Meeting Info</h6>
				<p
					ref={meetingDescriptionRef}
					className={cx(styles.meetingDescription, { [styles.extended]: showMoreDescription })}
					dangerouslySetInnerHTML={{ __html: newDescription }}
				/>

				{descriptionExpandable &&
					(!showMoreDescription ? (
						<span className={styles.showMoreLessButton} onClick={onDescriptionMoreClick}>
							read more <FontAwesomeIcon icon={faAngleDown} />
						</span>
					) : (
						<span className={styles.showMoreLessButton} onClick={onDescriptionLessClick}>
							read less <FontAwesomeIcon icon={faAngleUp} />
						</span>
					))}
			</div>
		) : null;

	return (
		<Modal className={styles.modal} onClose={onClose} data-test-id="meeting-info-modal">
			<Modal.Header>
				<Modal.Title data-test-id="meeting-info-modal-title">{event?.title}</Modal.Title>
				<Modal.HeaderActions onClose={onClose}>
					{hasEdit && <IconButton icon={faPen} onClick={onEditClick} iconSize="sm" />}
				</Modal.HeaderActions>
			</Modal.Header>

			<Modal.Body className={styles.body}>
				<div className={styles.row} data-test-id="meeting-info-modal-date">
					<div className={styles.rowIcon}>
						<FontAwesomeIcon fixedWidth icon={faCalendar} />
					</div>
					<div className={styles.rowInfo}>
						<p>{meetingDateFormat(event?.start || new Date(), event?.end || new Date(), Boolean(event?.isAllDay))}</p>
						{event?.recurrenceRule?.length && event?.recurrenceRule[0] && (
							<p>Repeats {recurrenceRuleToString(event?.recurrenceRule)}</p>
						)}
						{!isExternalUser && noteType !== "goalset" && onChangeLinkedMeeting && (
							<ButtonLink onClick={openNoteLinkingModal}>Change associated meeting</ButtonLink>
						)}
					</div>
				</div>

				<div className={styles.row}>
					<div className={styles.rowIcon}>
						<div
							className={styles.calendarColorDot}
							style={event?.calendar.color ? { backgroundColor: event.calendar.color } : {}}
						/>
					</div>
					<div className={styles.rowInfo}>
						{event?.calendar.sourcePrimary ? "Primary calendar" : event?.calendar.name}
					</div>
				</div>

				{event?.location && (
					<div className={styles.row} data-test-id="meeting-info-modal-location">
						<div className={styles.rowIcon}>
							<FontAwesomeIcon fixedWidth icon={faMapMarker} />
						</div>
						<div className={styles.rowInfo}>{urlToAnchorTag(event.location)}</div>
					</div>
				)}

				<>
					{loading ? (
						<>
							{callContent}
							{description}
							<div className={styles.loader}>
								<LoadingSpinner />
							</div>
						</>
					) : (
						<>
							{event?.userTodos && (
								<div className={styles.row} data-test-id="meeting-info-modal-actionitems">
									<div className={styles.rowIcon}>
										<FontAwesomeIcon fixedWidth icon={faCheckSquare} />
									</div>
									<div className={styles.rowInfo}>{remainingActionItems}</div>
								</div>
							)}
							{callContent}
							{showRecordIcon && (
								<div className={styles.row}>
									<div className={styles.rowIcon}>
										<RecordIcon />
									</div>
									<div className={styles.rowInfo}>
										Meeting will be recorded
										<ButtonLink onClick={openStreamSettingsModal}>Update settings</ButtonLink>
									</div>
								</div>
							)}
							{event?.attachments && event?.attachments.length > 0 && (
								<>
									<h6 className={styles.meetingHeader}>Attachments</h6>
									{event.attachments.map(attachment => (
										<div className={styles.row} key={`attachment-${attachment.fileId}`}>
											<div className={styles.rowIconSmall}>
												<RecordingsIcon provider={attachment.provider} />
											</div>
											<div className={styles.rowText}>
												<a
													href={attachment.url}
													target="_blank"
													rel="noopener noreferrer"
													title={attachment.title || undefined}
												>
													{attachment.title}
												</a>
											</div>
										</div>
									))}
								</>
							)}
							{description}
							{event?.id && (
								<div className={styles.attendeesWrapper}>
									<MeetingInfoModalAttendeesList
										attendees={compact(event.attendees) as AttendeeAttrs[]}
										eventId={event.id}
									/>
								</div>
							)}
						</>
					)}
				</>
			</Modal.Body>
			<Modal.Footer>
				<Modal.FooterSecondaryActions className={styles.footerSecondaryActions}>
					<div>Going?</div>
					<ButtonLink
						className={cx({ [styles.accepted]: event?.userResponseStatus === "accepted" })}
						onClick={handleAcceptedResponse}
					>
						Yes
					</ButtonLink>
					<ButtonLink
						className={cx({ [styles.accepted]: event?.userResponseStatus === "declined" })}
						onClick={handleDeclinedResponse}
					>
						No
					</ButtonLink>
					<ButtonLink
						className={cx({
							[styles.accepted]:
								event?.userResponseStatus === "tentative" || event?.userResponseStatus === "tentativelyAccepted",
						})}
						onClick={handleTenativeResponse}
					>
						Maybe
					</ButtonLink>
				</Modal.FooterSecondaryActions>
				{isFromMeetingsPanel && (
					<Button size="normal" variant="primary" onClick={onClickOpenNotes} data-test-id="meeting-info-modal-openbtn">
						Open Note
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
}
